
import React from "react";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { cn } from "@/lib/utils";
import { scrollToTop } from "@/hooks/useScrollToTop";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface ProductPaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  className?: string;
}

const ProductPagination = ({
  currentPage,
  totalPages,
  onPageChange,
  className,
}: ProductPaginationProps) => {
  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    
    // Always show first page
    if (currentPage > 2) {
      pages.push(1);
    }
    
    // Show ellipsis if needed
    if (currentPage > 3) {
      pages.push("ellipsis");
    }
    
    // Show previous page if not first
    if (currentPage > 1) {
      pages.push(currentPage - 1);
    }
    
    // Show current page
    pages.push(currentPage);
    
    // Show next page if not last
    if (currentPage < totalPages) {
      pages.push(currentPage + 1);
    }
    
    // Show ellipsis if needed
    if (currentPage < totalPages - 2) {
      pages.push("ellipsis");
    }
    
    // Always show last page
    if (currentPage < totalPages - 1 && totalPages > 1) {
      pages.push(totalPages);
    }
    
    return pages;
  };
  
  // Handle page change with scroll to top
  const handlePageChange = (page: number) => {
    if (page !== currentPage) {
      onPageChange(page);
      scrollToTop();
    }
  };

  return (
    <>
      {/* Desktop: numeric pagination unchanged */}
      <Pagination className={cn("py-6 hidden md:flex", className)}>
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious
              onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
              className={cn(
                currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"
              )}
            />
          </PaginationItem>
          
          {getPageNumbers().map((page, index) => (
            <PaginationItem key={index}>
              {page === "ellipsis" ? (
                <span className="px-4 py-2">...</span>
              ) : (
                <PaginationLink
                  isActive={page === currentPage}
                  className={cn(
                    "cursor-pointer",
                    page === currentPage ? "bg-primary text-primary-foreground hover:bg-primary/90" : ""
                  )}
                  onClick={() => page !== currentPage && handlePageChange(page as number)}
                >
                  {page}
                </PaginationLink>
              )}
            </PaginationItem>
          ))}
          
          <PaginationItem>
            <PaginationNext
              onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
              className={cn(
                currentPage === totalPages || totalPages === 0 ? "pointer-events-none opacity-50" : "cursor-pointer"
              )}
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>

      {/* Mobile: compact controls with icon buttons and a small indicator */}
      <Pagination className={cn("py-4 md:hidden", className)}>
        <PaginationContent className="items-center gap-3">
          <PaginationItem>
            <Button
              variant="outline"
              size="icon"
              aria-label="Previous page"
              onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
              disabled={currentPage <= 1}
              className="h-9 w-9"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
          </PaginationItem>

          <PaginationItem>
            <span className="px-2 text-xs font-medium text-gray-600 tabular-nums">
              Page {currentPage} of {totalPages}
            </span>
          </PaginationItem>

          <PaginationItem>
            <Button
              variant="outline"
              size="icon"
              aria-label="Next page"
              onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
              disabled={currentPage >= totalPages || totalPages === 0}
              className="h-9 w-9"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </>
  );
};

export default ProductPagination;
